import {
  AlertDialog,
  AlertDialogContent,
  Button,
  Card,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Separator,
} from "@pushpress/pushpress-ui";
import { InfoIcon, XIcon } from "lucide-react";
import { useState } from "react";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../utils/misc";
import { useCreateStripeCheckoutSession } from "../../../apis/billingApi.hooks";
import { loadStripe } from "@stripe/stripe-js";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import {
  getSubscriptionInfo,
  stripeCoreSubscriptionInfo,
  CoreSubscriptionDetails,
} from "../constants/subscriptionInfoMap";
import { useGetStripeBillingCustomerIdQuery } from "../../../apis/clientApi.hooks";
import { AccountEvent } from "../constants/account.events";

interface CoreSubscriptionCardProps {
  priceId: string;
  hasActiveSubscription?: boolean;
}

const stripePromise = loadStripe(process.env.STRIPE_SAAS_PUBLISHABLE_KEY);

const CoreSubscriptionCard: React.FC<CoreSubscriptionCardProps> = ({
  priceId,
  hasActiveSubscription = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const { mutateAsync: createStripeCheckoutSession } =
    useCreateStripeCheckoutSession();
  const {
    data,
    isError: isStripeBillingIdError,
    error,
  } = useGetStripeBillingCustomerIdQuery();
  const { trackEvent } = useAnalytics();

  const hasFailedToGetStripeBillingCustomerId =
    isStripeBillingIdError || !data?.stripeBillingCustomerId;

  const startCheckoutSession = async (
    subscriptionInfo: CoreSubscriptionDetails,
  ) => {
    trackEvent(AccountEvent.StartCheckoutSession, {
      priceId,
      product: subscriptionInfo.title,
      location: "Account Page",
      subSection: "Core subscription card",
    });
    setIsLoading(true);
    if (hasFailedToGetStripeBillingCustomerId) {
      trackEvent(AccountEvent.StripeBillingCustomerIdNotFound, {
        error,
        priceId,
        location: "Account Page",
        subSection: "Core subscription card",
      });
      setIsLoading(false);
      toast.error(
        "Failed to get account information, please refresh the page and try again or contact support.",
        defaultToastOptions,
      );
      return;
    }

    try {
      await createStripeCheckoutSession(
        {
          priceId,
          customerId: data?.stripeBillingCustomerId,
          returnUrl: window.location.href,
        },
        {
          onSuccess: (response) => {
            setClientSecret(response.clientSecret);
            setShowDialog(true);
            return;
          },
        },
      );
    } catch (error) {
      trackEvent(AccountEvent.CreateCheckoutSessionFailed, {
        error,
        priceId,
        location: "Account Page",
        subSection: "Core subscription card",
      });
      toast.error(
        `Could not open purchase page, please try again. ${error}`,
        defaultToastOptions,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const subscriptionInfo = getSubscriptionInfo(
    stripeCoreSubscriptionInfo,
    priceId,
  );

  if (!subscriptionInfo) {
    trackEvent(AccountEvent.PlanInformationNotFound, {
      priceId,
      location: "Account Page",
      subSection: "Core subscription card",
    });
    return null;
  }

  return (
    <>
      <Card className="tw-flex-1 tw-min-w-[280px] tw-max-w-[325px] tw-h-[320px]">
        <div className="tw-p-8 tw-flex tw-flex-col tw-h-full">
          <div className="tw-flex tw-justify-between">
            <div className="tw-flex-col">
              <h3 className="tw-text-xl">{subscriptionInfo.title}</h3>
              <p className="tw-text-neutral-500 tw-text-[16px]">
                {subscriptionInfo.subscriptionPricing}
              </p>
            </div>
            <HoverCard>
              <HoverCardTrigger>
                <InfoIcon className="tw-text-neutral-500" size={24} />
              </HoverCardTrigger>
              <HoverCardContent>
                <ul className="tw-list-disc tw-list-outside tw-text-neutral-500 tw-p-5 tw-mb-2 tw-hyphens-auto">
                  {subscriptionInfo.productDetails.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </HoverCardContent>
            </HoverCard>
          </div>
          <p className="tw-mt-6 tw-text-[16px]">
            {subscriptionInfo.description}
          </p>
          <div className="tw-flex tw-flex-col tw-h-full">
            <div className="tw-flex-grow"></div>
            {hasActiveSubscription ? (
              <p className="tw-mt-[84px] tw-text-neutral-500 tw-text-center tw-uppercase">
                Your current plan
              </p>
            ) : (
              <div className="tw-flex tw-flex-col tw-gap-4">
                <a
                  className="tw-text-center tw-font-semibold"
                  href="https://www.pushpress.com/pricing"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    trackEvent(AccountEvent.LearnMoreClicked, {
                      priceId,
                      product: subscriptionInfo.title,
                      location: "Account Page",
                      subSection: "Core subscription card",
                    });
                  }}
                >
                  Learn more
                </a>
                <Button
                  onClick={() => startCheckoutSession(subscriptionInfo)}
                  disabled={isLoading}
                  isSubmitting={isLoading}
                >
                  Upgrade
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div>
        <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
          <AlertDialogContent>
            <div className="tw-w-full tw-h-[600px] tw-max-h-[80vh] tw-overflow-y-auto tw-overflow-x-hidden">
              <div className="tw-flex tw-items-center tw-justify-between tw-pb-5">
                <XIcon
                  onClick={() => setShowDialog(false)}
                  className="tw-cursor-pointer"
                />
                <p className="tw-text-xl">Upgrade plan</p>
                <div className="tw-invisible">
                  <XIcon className="tw-cursor-pointer" />
                </div>
              </div>
              <Separator className="tw-mb-4" orientation="horizontal" />
              {hasFailedToGetStripeBillingCustomerId ? (
                <p>Failed to get stripe billing customer id</p>
              ) : (
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={{ clientSecret }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              )}
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
};

export default CoreSubscriptionCard;
